import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const DuelStats = ({ duels, userId, badges }) => {
  // Calculate statistics
  const completedDuels = duels?.filter(d => d?.status === 'completed') || [];
  const wins = completedDuels?.filter(d => d?.winner_id === userId)?.length || 0;
  const losses = completedDuels?.filter(d => d?.winner_id && d?.winner_id !== userId)?.length || 0;
  const draws = completedDuels?.filter(d => !d?.winner_id)?.length || 0;
  const activeDuels = duels?.filter(d => d?.status === 'active')?.length || 0;
  const totalDuels = completedDuels?.length;
  const winRate = totalDuels > 0 ? Math?.round((wins / totalDuels) * 100) : 0;

  // Calculate stats by metric
  const metricStats = {};
  completedDuels?.forEach(duel => {
    if (!metricStats?.[duel?.metric]) {
      metricStats[duel?.metric] = { total: 0, wins: 0, losses: 0 };
    }
    metricStats[duel?.metric].total++;
    if (duel?.winner_id === userId) {
      metricStats[duel?.metric].wins++;
    } else if (duel?.winner_id) {
      metricStats[duel?.metric].losses++;
    }
  });

  const metricLabels = {
    gym_workouts: { label: 'Gym', icon: 'Dumbbell' },
    calories_limit: { label: 'Calorías', icon: 'Flame' },
    sleep_hours: { label: 'Sueño', icon: 'Moon' },
    water_intake: { label: 'Agua', icon: 'Droplets' },
    daily_steps: { label: 'Pasos', icon: 'Footprints' },
    daily_minimum: { label: 'Mínimos', icon: 'CheckCircle' }
  };

  // Social badges
  const socialBadges = badges?.filter(b => b?.category === 'social') || [];

  return (
    <div className="space-y-6">
      {/* Overall Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0 }}
          className="p-6 bg-card rounded-xl border-2 border-border"
        >
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center">
              <Icon name="Trophy" size={24} className="text-success" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Victorias</p>
              <p className="text-3xl font-bold text-success">{wins}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="p-6 bg-card rounded-xl border-2 border-border"
        >
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-12 h-12 rounded-full bg-error/10 flex items-center justify-center">
              <Icon name="X" size={24} className="text-error" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Derrotas</p>
              <p className="text-3xl font-bold text-error">{losses}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="p-6 bg-card rounded-xl border-2 border-border"
        >
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon name="Percent" size={24} className="text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Win Rate</p>
              <p className="text-3xl font-bold text-primary">{winRate}%</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="p-6 bg-card rounded-xl border-2 border-border"
        >
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-12 h-12 rounded-full bg-warning/10 flex items-center justify-center">
              <Icon name="Flame" size={24} className="text-warning" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Activos</p>
              <p className="text-3xl font-bold text-warning">{activeDuels}</p>
            </div>
          </div>
        </motion.div>
      </div>
      {/* Performance by Metric */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="p-6 bg-card rounded-xl border-2 border-border"
      >
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center space-x-2">
          <Icon name="BarChart3" size={20} />
          <span>Rendimiento por Categoría</span>
        </h3>
        <div className="space-y-4">
          {Object?.entries(metricStats)?.map(([metric, stats]) => {
            const metricInfo = metricLabels?.[metric] || { label: metric, icon: 'Target' };
            const metricWinRate = stats?.total > 0 ? Math?.round((stats?.wins / stats?.total) * 100) : 0;

            return (
              <div key={metric} className="p-4 bg-muted/20 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <Icon name={metricInfo?.icon} size={20} className="text-primary" />
                    <span className="font-medium text-foreground">{metricInfo?.label}</span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-primary">{metricWinRate}% win rate</p>
                    <p className="text-xs text-muted-foreground">{stats?.total} duelos</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="flex-1 bg-muted/50 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full bg-success" 
                      style={{ width: `${metricWinRate}%` }}
                    />
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {stats?.wins}W - {stats?.losses}L
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>
      {/* Social Badges */}
      {socialBadges?.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="p-6 bg-card rounded-xl border-2 border-border"
        >
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center space-x-2">
            <Icon name="Award" size={20} />
            <span>Logros Sociales</span>
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {socialBadges?.map((badge, index) => (
              <motion.div
                key={badge?.id}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5 + index * 0.05 }}
                className="p-4 bg-muted/20 rounded-xl text-center"
              >
                <div className={`w-16 h-16 rounded-full bg-${badge?.badge_color}/10 flex items-center justify-center mx-auto mb-2`}>
                  <span className="text-3xl">{badge?.badge_icon}</span>
                </div>
                <p className="text-sm font-semibold text-foreground">{badge?.badge_name}</p>
                <p className="text-xs text-muted-foreground mt-1">{badge?.badge_description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}
      {/* Recent Duels Summary */}
      {completedDuels?.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="p-6 bg-card rounded-xl border-2 border-border"
        >
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center space-x-2">
            <Icon name="History" size={20} />
            <span>Historial Reciente</span>
          </h3>
          <div className="space-y-3">
            {completedDuels?.slice(0, 5)?.map((duel) => {
              const isWinner = duel?.winner_id === userId;
              const opponent = duel?.creator_id === userId ? duel?.opponent : duel?.creator;

              return (
                <div key={duel?.id} className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      isWinner ? 'bg-success/10' : 'bg-error/10'
                    }`}>
                      <Icon 
                        name={isWinner ? 'Check' : 'X'} 
                        size={16} 
                        className={isWinner ? 'text-success' : 'text-error'}
                      />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">{duel?.title}</p>
                      <p className="text-xs text-muted-foreground">vs {opponent?.full_name}</p>
                    </div>
                  </div>
                  <span className={`text-sm font-semibold ${
                    isWinner ? 'text-success' : 'text-error'
                  }`}>
                    {isWinner ? 'Victoria' : 'Derrota'}
                  </span>
                </div>
              );
            })}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default DuelStats;